import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface B3DsRadioOption {
  value: string;
  label: string;
}

@Component({
  standalone: false,
  selector: 'b3-ds-radio-group',
  template: `
    <div class="b3-ds-radio-group" role="radiogroup">
      <b3-radio-button
        *ngFor="let option of options"
        [attr.name]="name"
        [value]="option.value"
        [label]="option.label"
        [selected]="value === option.value"
        [error]="error"
        [disabled]="isDisabled"
        (b3-change)="onRadioChange($event, option.value)">
      </b3-radio-button>
    </div>
  `,
  styles: [`
    .b3-ds-radio-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsRadioGroupComponent),
      multi: true,
    },
  ],
})
export class B3DsRadioGroupComponent implements ControlValueAccessor {
  @Input() name = 'b3-ds-radio-group';
  @Input() options: B3DsRadioOption[] = [];
  @Input() error = false;
  @Output() valueChange = new EventEmitter<string>();

  value = '';
  isDisabled = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onRadioChange(event: Event, optionValue: string): void {
    const customEvent = event as CustomEvent<{ selected?: boolean }>;
    if (!customEvent.detail?.selected) {
      return;
    }

    this.value = optionValue;
    this.onChange(optionValue);
    this.onTouched();
    this.valueChange.emit(optionValue);
    this.cdr.markForCheck();
  }
}
